/* eslint-disable react-hooks/exhaustive-deps, react/no-children-prop */
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Card, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { green, grey, yellow } from "@mui/material/colors";
import dayjs, { Dayjs } from "dayjs";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { RefreshButton, useAppBar } from "../../../components";
import { AttendanceLogEntry, Child, DailyInspection, ServicePeriod } from "../../../models";
import {
  useGetAllAttendanceLogEntriesFilteredQuery,
  useGetAllChildrenFilteredQuery,
  useGetAllServicePeriodsQuery,
  useGetDailyInspectionsFilteredQuery,
  useGetLooseEndsQuery,
} from "../../../redux/apiStore";
import { formatNumberAsCurrency, getChildImageUrlFromChild, getCurrentDateLongNoYear } from "../../../utils";
import DayDetailDrawer, { CDrawerView } from "../../calendar/main/DayDetailDrawer";

function getChildFullName(child?: Child) {
  if (!child) return "Unknown child";
  const fullName = `${child.firstName ?? ""} ${child.lastName ?? ""}`.trim();
  return fullName.length > 0 ? fullName : `Child #${child.id}`;
}

function isLogForDate(logDateTime: string | undefined, targetDate: string) {
  if (!logDateTime) return false;
  const localMatch = dayjs(logDateTime).format("YYYY-MM-DD") === targetDate;
  const rawPrefixMatch = logDateTime.startsWith(targetDate);
  return localMatch || rawPrefixMatch;
}

function getStatusDescription(log?: AttendanceLogEntry) {
  if (!log || !log.timeInCentralDateTime) {
    return "Absent";
  }

  if (log.timeOutCentralDateTime) {
    return `Signed out at ${dayjs(log.timeOutCentralDateTime).format("h:mm A")}`;
  }

  return `Signed in at ${dayjs(log.timeInCentralDateTime).format("h:mm A")}`;
}

function getStatusColor(log?: AttendanceLogEntry) {
  const isAbsent = !log;
  const isSignedIn = !!log && !log.timeOutCentralDateTime;

  if (isAbsent) {
    return grey[500];
  }

  if (isSignedIn) {
    return green[500];
  }

  return yellow[700];
}

function isEnrolledOnDate(child: Child, targetDate: dayjs.Dayjs) {
  const day = targetDate.startOf("day");
  const start = child.enrollmentStartCentralDateTime ? dayjs(child.enrollmentStartCentralDateTime).startOf("day") : null;
  const end = child.enrollmentEndCentralDateTime ? dayjs(child.enrollmentEndCentralDateTime).startOf("day") : null;

  if (start && day.isBefore(start)) {
    return false;
  }

  if (end && day.isAfter(end)) {
    return false;
  }

  return true;
}

function isServicePeriodPastDue(period: ServicePeriod): boolean {
  if (period.receiptId) {
    return false; // Already paid
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = new Date(period.startCentralDateTime);
  return today >= startDate;
}

function isInspectionIncomplete(inspection: DailyInspection | undefined): boolean {
  if (!inspection) return false;
  if (!inspection.inspectionCompletedCentralDateTime) return true;
  const completedYear = dayjs(inspection.inspectionCompletedCentralDateTime).year();
  return completedYear <= 2000; // Placeholder year means not completed
}

function getServicePeriodTotal(period: ServicePeriod): number {
  return (period.lines ?? []).reduce((sum, line) => sum + (line.estimatedTotal ?? 0), 0);
}

export function HomePage() {
  const appBar = useAppBar();
  const today = dayjs();
  const todayDate = today.format("YYYY-MM-DD");
  const [drawerDate, setDrawerDate] = useState<Dayjs | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerInitialView, setDrawerInitialView] = useState<CDrawerView.ATTENDANCE_EDIT | CDrawerView.DAILY_INSPECTION_EDIT | undefined>(undefined);

  const childrenQuery = useGetAllChildrenFilteredQuery({});
  const looseEndsQuery = useGetLooseEndsQuery({});
  const servicePeriodsQuery = useGetAllServicePeriodsQuery({});
  const attendanceTodayQuery = useGetAllAttendanceLogEntriesFilteredQuery({
    rangeStartDate: today.subtract(1, "day").startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    rangeEndDate: today.add(1, "day").endOf("day").format("YYYY-MM-DD HH:mm:ss"),
  });

  // Fetch daily inspections for the past 6 months to check for incomplete inspections
  const sixMonthsAgo = today.subtract(6, "months").startOf("day");
  const incompleteInspectionsQuery = useGetDailyInspectionsFilteredQuery({
    rangeStartInspectionDate: sixMonthsAgo.format("YYYY-MM-DD"),
    rangeEndInspectionDate: today.format("YYYY-MM-DD"),
  });

  // Fetch daily inspection and attendance for the selected drawer date
  const drawerDateStr = drawerDate?.format("YYYY-MM-DD") ?? "";
  const dailyInspectionsQuery = useGetDailyInspectionsFilteredQuery({
    rangeStartInspectionDate: drawerDateStr,
    rangeEndInspectionDate: drawerDateStr,
  });
  const drawerAttendanceQuery = useGetAllAttendanceLogEntriesFilteredQuery({
    rangeStartDate: drawerDate?.subtract(1, "day").startOf("day").format("YYYY-MM-DD HH:mm:ss") ?? "",
    rangeEndDate: drawerDate?.add(1, "day").endOf("day").format("YYYY-MM-DD HH:mm:ss") ?? "",
  });

  const childrenById = useMemo(() => {
    return new Map((childrenQuery.data ?? []).map((child) => [child.id, child]));
  }, [childrenQuery.data]);

  const todayAttendanceByChildId = useMemo(() => {
    const todaysLogs = (attendanceTodayQuery.data ?? []).filter((entry) => isLogForDate(entry.timeInCentralDateTime, todayDate));
    const sorted = _.orderBy(todaysLogs, [(entry) => entry.timeInCentralDateTime ?? ""], ["desc"]);
    return new Map(sorted.map((entry) => [entry.childId, entry]));
  }, [attendanceTodayQuery.data, todayDate]);

  const activeEnrolledChildren = useMemo(() => {
    return _.orderBy(
      (childrenQuery.data ?? []).filter((child) => child.isActive && !child.deleted && isEnrolledOnDate(child, today)),
      [(child) => `${child.firstName ?? ""} ${child.lastName ?? ""}`.toLowerCase()],
      ["asc"],
    );
  }, [childrenQuery.data, today]);

  const looseEnds = useMemo(() => {
    return _.orderBy(looseEndsQuery.data ?? [], [(entry) => entry.timeInCentralDateTime ?? ""], ["desc"]);
  }, [looseEndsQuery.data]);

  const pastDueServicePeriods = useMemo(() => {
    return _.orderBy(
      (servicePeriodsQuery.data ?? []).filter((period) => !period.deleted && isServicePeriodPastDue(period)),
      [(period) => period.startCentralDateTime],
      ["desc"],
    );
  }, [servicePeriodsQuery.data]);

  const incompleteInspections = useMemo(() => {
    return (
      incompleteInspectionsQuery.data
        ?.filter((inspection) => !inspection.isClosed && isInspectionIncomplete(inspection))
        .sort((a, b) => {
          const dateA = dayjs(a.inspectionCentralDateTime);
          const dateB = dayjs(b.inspectionCentralDateTime);
          return dateB.valueOf() - dateA.valueOf();
        }) ?? []
    );
  }, [incompleteInspectionsQuery.data]);

  const openDrawer = (date: Dayjs, initialView?: CDrawerView.ATTENDANCE_EDIT | CDrawerView.DAILY_INSPECTION_EDIT) => {
    setDrawerDate(date);
    setDrawerInitialView(initialView);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setDrawerInitialView(undefined);
  };

  const refreshDashboard = () => {
    Promise.all([
      childrenQuery.refetch(),
      looseEndsQuery.refetch(),
      servicePeriodsQuery.refetch(),
      attendanceTodayQuery.refetch(),
      incompleteInspectionsQuery.refetch(),
    ]);
  };

  useEffect(() => {
    appBar.setDefaults();
    appBar.setTitle(getCurrentDateLongNoYear());
    appBar.setRightContent(<RefreshButton onRefresh={refreshDashboard} />);
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (!document.hidden) {
        refreshDashboard();
      }
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <>
      <Stack gap={4} sx={{ p: { xs: 1, md: 2 } }}>
        {(looseEnds.length > 0 || pastDueServicePeriods.length > 0 || incompleteInspections.length > 0) && (
          <Stack gap={1.5}>
            {(looseEndsQuery.isFetching || servicePeriodsQuery.isFetching || incompleteInspectionsQuery.isFetching) && (
              <Stack direction="row" sx={{ py: 2, justifyContent: "center" }}>
                <CircularProgress size={28} />
              </Stack>
            )}

            {!looseEndsQuery.isFetching && !servicePeriodsQuery.isFetching && !incompleteInspectionsQuery.isFetching && looseEndsQuery.isError && (
              <Card sx={{ p: 2, borderRadius: 3 }}>
                <Typography color="error">Failed to load alerts.</Typography>
              </Card>
            )}

            {!looseEndsQuery.isFetching && !servicePeriodsQuery.isFetching && !incompleteInspectionsQuery.isFetching && !looseEndsQuery.isError && (
              <Stack gap={1.25} sx={{ display: "flex", flexWrap: "wrap", flexDirection: "row" }}>
                {/* Incomplete Daily Inspections */}
                {incompleteInspections.map((inspection) => {
                  const inspectionDate = dayjs(inspection.inspectionCentralDateTime);
                  const dateStr = inspectionDate.format("M/D/YY");
                  const daysAgo = today.diff(inspectionDate, "day");
                  const daysAgoStr = daysAgo === 0 ? "Today" : daysAgo === 1 ? "Yesterday" : `${daysAgo} days ago`;

                  return (
                    <Card
                      key={`incomplete-inspection-${inspection.id}`}
                      onClick={() => openDrawer(inspectionDate)}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        width: { xs: "100%", md: 320 },
                        cursor: "pointer",
                        border: "1px solid",
                        borderColor: "error.light",
                        backgroundColor: "error.50",
                        transition: "box-shadow 120ms ease, transform 120ms ease",
                        "&:hover": {
                          boxShadow: 3,
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      <Stack gap={1}>
                        <Stack direction="row" gap={0.75} sx={{ alignItems: "center" }}>
                          <Box sx={{ color: "error.main", fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                          </Box>
                          <Typography sx={{ fontSize: 13, color: "error.dark", fontWeight: 600 }}>Daily Inspection Not Complete</Typography>
                        </Stack>
                        <Stack gap={0.5}>
                          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                            {dateStr} ({daysAgoStr})
                          </Typography>
                        </Stack>
                      </Stack>
                    </Card>
                  );
                })}

                {/* Loose Ends Alerts */}
                {looseEnds.map((entry) => {
                  const child = childrenById.get(entry.childId);
                  const childName = getChildFullName(child);
                  const looseEndDate = entry.timeInCentralDateTime ? dayjs(entry.timeInCentralDateTime).format("M/D/YY") : "Unknown date";
                  const looseEndDay = entry.timeInCentralDateTime ? dayjs(entry.timeInCentralDateTime) : null;

                  return (
                    <Card
                      key={`loose-end-${entry.id}`}
                      onClick={looseEndDay ? () => openDrawer(looseEndDay, CDrawerView.ATTENDANCE_EDIT) : undefined}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        width: { xs: "100%", md: 320 },
                        cursor: looseEndDay ? "pointer" : "default",
                        border: "1px solid",
                        borderColor: "warning.light",
                        backgroundColor: "warning.50",
                        transition: "box-shadow 120ms ease, transform 120ms ease",
                        "&:hover": looseEndDay
                          ? {
                              boxShadow: 3,
                              transform: "translateY(-1px)",
                            }
                          : undefined,
                      }}
                    >
                      <Stack gap={1}>
                        <Stack direction="row" gap={0.75} sx={{ alignItems: "center" }}>
                          <Box sx={{ color: "warning.main", fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                          </Box>
                          <Typography sx={{ fontSize: 13, color: "warning.dark", fontWeight: 600 }}>Loose End</Typography>
                        </Stack>
                        <Stack direction="row" gap={1} sx={{ alignItems: "center" }}>
                          <Avatar alt={childName} src={getChildImageUrlFromChild(child)} sx={{ width: 32, height: 32 }} />
                          <Stack sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden", fontSize: 14, fontWeight: 500 }}>
                              {childName}
                            </Typography>
                            <Typography sx={{ color: "text.secondary", fontSize: 12 }}>{looseEndDate}</Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Card>
                  );
                })}

                {/* Past Due Service Periods */}
                {pastDueServicePeriods.map((period) => {
                  const startDate = dayjs(period.startCentralDateTime).format("M/D/YY");
                  const endDate = dayjs(period.endCentralDateTime).format("M/D/YY");
                  const total = getServicePeriodTotal(period);
                  const uniqueChildren = Array.from(new Map((period.lines ?? []).map((line) => [line.childId, line.child])).values()).filter(
                    (child) => child != null,
                  ) as Child[];

                  return (
                    <Card
                      key={`past-due-${period.id}`}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        width: { xs: "100%", md: 320 },
                        border: "1px solid",
                        borderColor: "error.light",
                        backgroundColor: "error.50",
                      }}
                    >
                      <Stack gap={1}>
                        <Stack direction="row" gap={0.75} sx={{ alignItems: "center" }}>
                          <Box sx={{ color: "error.main", fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                          </Box>
                          <Typography sx={{ fontSize: 13, color: "error.dark", fontWeight: 600 }}>Payment Past Due</Typography>
                        </Stack>
                        <Stack gap={0.75}>
                          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                            <Typography sx={{ fontSize: 12, color: "text.secondary", flex: 1 }}>
                              {startDate} - {endDate}
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "error.dark" }}>{formatNumberAsCurrency(total)}</Typography>
                          </Stack>
                          <Stack direction="row" gap={0.5} sx={{ flexWrap: "wrap" }}>
                            {uniqueChildren.map((child) => (
                              <Stack key={child.id} direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                                <Tooltip key={child.id} title={getChildFullName(child)}>
                                  <Avatar alt={getChildFullName(child)} src={getChildImageUrlFromChild(child)} sx={{ width: 28, height: 28 }} />
                                </Tooltip>
                                <Typography sx={{ fontSize: 12, color: "text.secondary", ml: 0.5 }}>{getChildFullName(child)}</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Stack>
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Stack>
        )}

        <Stack gap={1.5}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Attendance Today
          </Typography>

          {(childrenQuery.isFetching || attendanceTodayQuery.isFetching) && (
            <Stack direction="row" sx={{ py: 2, justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Stack>
          )}

          {!childrenQuery.isFetching && !attendanceTodayQuery.isFetching && (childrenQuery.isError || attendanceTodayQuery.isError) && (
            <Card sx={{ p: 2, borderRadius: 3 }}>
              <Typography color="error">Failed to load attendance for today.</Typography>
            </Card>
          )}

          {!childrenQuery.isFetching && !attendanceTodayQuery.isFetching && !childrenQuery.isError && !attendanceTodayQuery.isError && (
            <Stack direction="row" gap={1.5} sx={{ overflowX: "auto", pb: 0.5 }}>
              {activeEnrolledChildren.map((child) => {
                const attendanceLog = todayAttendanceByChildId.get(child.id);
                const statusColor = getStatusColor(attendanceLog);
                const childName = getChildFullName(child);

                return (
                  <Card
                    key={child.id}
                    onClick={() => openDrawer(today, CDrawerView.ATTENDANCE_EDIT)}
                    sx={{
                      minWidth: 132,
                      p: 1.5,
                      borderRadius: 2.5,
                      border: "1px solid",
                      borderColor: "grey.200",
                      flexShrink: 0,
                      cursor: "pointer",
                      transition: "box-shadow 120ms ease, transform 120ms ease",
                      "&:hover": {
                        boxShadow: 3,
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Stack gap={1} alignItems="center">
                      <Box sx={{ position: "relative", width: "fit-content" }}>
                        <Avatar alt={childName} src={getChildImageUrlFromChild(child)} sx={{ width: 80, height: 80 }} />
                        <Box
                          sx={{
                            position: "absolute",
                            top: -3,
                            left: -3,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            border: "3px solid",
                            borderColor: "common.white",
                            backgroundColor: statusColor,
                          }}
                        />
                      </Box>

                      <Stack gap={0.25} alignItems="center">
                        <Typography sx={{ fontWeight: 400, lineHeight: 1.2, fontSize: 15 }}>{childName}</Typography>
                        <Typography sx={{ color: "text.secondary", fontSize: 13 }}>{getStatusDescription(attendanceLog)}</Typography>
                      </Stack>
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Stack>
      {drawerDate && (
        <DayDetailDrawer
          selectedDate={drawerDate}
          dailyInspection={dailyInspectionsQuery.data?.find((x) => x.inspectionCentralDateTime?.startsWith(drawerDate.format("YYYY-MM-DD")))}
          attendanceLogEntries={(drawerAttendanceQuery.data ?? []).filter(
            (x) => dayjs(x.timeInCentralDateTime).format("YYYY-MM-DD") === drawerDate.format("YYYY-MM-DD"),
          )}
          children={childrenQuery.data ?? []}
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          initialView={drawerInitialView}
        />
      )}
    </>
  );
}
